import { getSessionSubject } from './token';

const KEY_PREFIX = 'astur:last-org:v1:';
const LEGACY_KEYS = ['lastVisitedOrganization', 'globalLastVisitedOrganization', 'last_visited_organization'];

const keyForSubject = (subject: string | null): string | null => {
  return subject ? `${KEY_PREFIX}${encodeURIComponent(subject)}` : null;
};

export const removeLegacyLastVisitedOrganizationKeys = (): void => {
  if (typeof window === 'undefined') return;
  try {
    for (const key of LEGACY_KEYS) {
      window.sessionStorage.removeItem(key);
      window.localStorage.removeItem(key);
    }
  } catch {
    // Storage can be unavailable in privacy-restricted browsing contexts.
  }
};

const readLegacyLastVisitedOrganization = (): string | null => {
  try {
    for (const key of LEGACY_KEYS) {
      const sessionValue = window.sessionStorage.getItem(key);
      if (sessionValue) return sessionValue;
      const localValue = window.localStorage.getItem(key);
      if (localValue) return localValue;
    }
  } catch {
    // Storage can be unavailable in privacy-restricted browsing contexts.
  }
  return null;
};

/**
 * One-shot adoption of the pre-session-module unscoped hint under the current
 * subject's key. Runs from KeycloakSessionBridge once an authenticated client
 * is committed — never from a getter, which render code may call. The legacy
 * keys are removed only after the scoped replacement is durably written; on
 * any failure (or with no subject) the hint is left in place for a later
 * attempt.
 */
export const migrateLegacyLastVisitedOrganization = (subject = getSessionSubject()): void => {
  if (typeof window === 'undefined') return;
  const key = keyForSubject(subject);
  if (!key) return;
  try {
    const scopedValue = window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key);
    if (scopedValue) {
      removeLegacyLastVisitedOrganizationKeys();
      return;
    }

    const legacyValue = readLegacyLastVisitedOrganization();
    if (!legacyValue) return;
    window.sessionStorage.setItem(key, legacyValue);
    window.localStorage.setItem(key, legacyValue);
    removeLegacyLastVisitedOrganizationKeys();
  } catch {
    // Storage can be unavailable in privacy-restricted browsing contexts;
    // keep the legacy hint so a later attempt can migrate it.
  }
};

// Pure read: main-sidebar calls this during render, so it must not write or
// remove storage. The read-only legacy fallback covers the window before the
// bridge's migration effect has run.
export const getLastVisitedOrganization = (subject = getSessionSubject()): string | null => {
  if (typeof window === 'undefined') return null;
  const key = keyForSubject(subject);
  if (!key) return null;
  try {
    return window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key) ?? readLegacyLastVisitedOrganization();
  } catch {
    return null;
  }
};

export const setLastVisitedOrganization = (organization: string, subject = getSessionSubject()): void => {
  if (typeof window === 'undefined' || !organization) return;
  const key = keyForSubject(subject);
  if (!key) return;
  try {
    window.sessionStorage.setItem(key, organization);
    window.localStorage.setItem(key, organization);
    // Only after the scoped write is durable: removing first would drop the
    // last-visited hint entirely if the scoped write then failed.
    removeLegacyLastVisitedOrganizationKeys();
  } catch {
    // The navigation preference is optional.
  }
};
